import React, { useRef, useState } from "react";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";

interface FileDropzoneProps {
  onFileSelect?: (file: File) => void;
  onFilesChange?: (files: File[]) => void;
  onClear?: () => void;
  selectedFile?: File;
  selectedFiles?: File[];
  acceptedFileExtensions?: string[]; // e.g. ['.xlsx', '.xls']
  maxFileSize?: number; // in bytes
  maxFiles?: number;
  maxTotalFileSize?: number; // in bytes
  multiple?: boolean;
  disabled?: boolean;
  className?: string;
  instructionText?: string;
  inputId?: string;
}

const formatFileSize = (bytes: number) => {
  if (bytes >= 1024 * 1024) {
    return `${Math.round(bytes / (1024 * 1024))} MB`;
  }
  return `${Math.round(bytes / 1024)} KB`;
};

export const FileDropzone: React.FC<FileDropzoneProps> = ({
  onFileSelect,
  onFilesChange,
  onClear,
  selectedFile,
  selectedFiles = [],
  acceptedFileExtensions = [".xlsx", ".xls"],
  maxFileSize,
  maxFiles = 1,
  maxTotalFileSize,
  multiple = false,
  disabled = false,
  className = "",
  instructionText = "Excel files only (.xlsx, .xls)",
  inputId,
}) => {
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const currentFiles = multiple
    ? selectedFiles
    : selectedFile
    ? [selectedFile]
    : [];

  const validateAndSetFiles = (newFiles: File[]) => {
    const normalizedExtensions = acceptedFileExtensions.map((extension) =>
      extension.toLowerCase()
    );
    const invalidFile = newFiles.find((file) => {
      const extensionPart = file.name.split(".").pop();
      const extension = `.${extensionPart?.toLowerCase() ?? ""}`;
      return (
        normalizedExtensions.length > 0 &&
        !normalizedExtensions.includes(extension)
      );
    });

    if (invalidFile) {
      toast.error(
        `Upload failed: Only ${acceptedFileExtensions.join(
          ", "
        )} files are allowed`
      );
      return;
    }

    const oversizedFile = maxFileSize
      ? newFiles.find((file) => file.size > maxFileSize)
      : undefined;
    if (oversizedFile && maxFileSize) {
      toast.error(
        `Upload failed: ${oversizedFile.name} must be ${formatFileSize(
          maxFileSize
        )} or smaller`
      );
      return;
    }

    if (!multiple) {
      onFileSelect?.(newFiles[0]);
      return;
    }

    const nextFiles = [...selectedFiles, ...newFiles];
    if (nextFiles.length > maxFiles) {
      toast.error(`Upload failed: You can attach up to ${maxFiles} files`);
      return;
    }

    const totalFileSize = nextFiles.reduce(
      (total, file) => total + file.size,
      0
    );
    if (maxTotalFileSize && totalFileSize > maxTotalFileSize) {
      toast.error(
        `Upload failed: Attachments must total ${formatFileSize(
          maxTotalFileSize
        )} or less`
      );
      return;
    }

    onFilesChange?.(nextFiles);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragActive(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    if (disabled) return;

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      validateAndSetFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleManualFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      validateAndSetFiles(Array.from(e.target.files));
    }
    // Reset input so same file can be selected again
    if (e.target) e.target.value = "";
  };

  const handleZoneClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  const handleZoneKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!disabled && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      fileInputRef.current?.click();
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onClear) {
      onClear();
    }
  };

  const handleRemove = (e: React.MouseEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    onFilesChange?.(
      selectedFiles.filter((_, fileIndex) => fileIndex !== index)
    );
  };

  return (
    <div className={`w-full ${className}`}>
      <div
        onClick={handleZoneClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onKeyDown={handleZoneKeyDown}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        aria-label="Upload files"
        className={`flex flex-col items-center justify-center w-full min-h-[160px] border-2 border-dashed rounded-lg transition-colors duration-200 ${
          isDragActive && !disabled
            ? "border-blue-500 bg-blue-50"
            : disabled
            ? "border-gray-200 bg-gray-50 cursor-not-allowed"
            : "border-gray-300 bg-gray-50 hover:bg-gray-100 cursor-pointer"
        }`}
      >
        <div className="flex flex-col items-center justify-center w-full h-full p-6 pointer-events-none">
          <Icon
            icon="material-symbols:cloud-upload-outline"
            className={`mb-3 ${disabled ? "text-gray-300" : "text-gray-400"}`}
            height={48}
            width={48}
          />
          <p
            className={`text-sm mb-1 ${
              disabled ? "text-gray-400" : "text-gray-600"
            }`}
          >
            <span
              className={`font-semibold ${
                disabled ? "text-gray-400" : "text-blue-600 hover:underline"
              }`}
              onClick={handleZoneClick}
            >
              Click to upload
            </span>{" "}
            or drag and drop
          </p>
          <p
            className={`text-xs ${
              disabled ? "text-gray-400" : "text-gray-500"
            }`}
          >
            {instructionText}
          </p>

          {!multiple && selectedFile && (
            <div className="mt-4 flex items-center gap-2 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm border border-blue-100 pointer-events-auto">
              <Icon icon="mdi:file-excel-outline" height={16} />
              <span className="font-medium truncate max-w-[200px]">
                {selectedFile.name}
              </span>
              {onClear && !disabled && (
                <button
                  onClick={handleClear}
                  className="ml-1 hover:text-red-500 transition-colors"
                  type="button"
                >
                  <Icon icon="mdi:close-circle" height={16} />
                </button>
              )}
            </div>
          )}

          {multiple && currentFiles.length > 0 && (
            <div className="mt-4 flex w-full flex-wrap items-center justify-center gap-2 pointer-events-auto">
              {currentFiles.map((file, index) => (
                <div
                  key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
                  className="flex max-w-full items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-sm text-blue-700"
                >
                  <Icon icon="mdi:file-excel-outline" height={16} />
                  <span
                    className="max-w-[200px] truncate font-medium"
                    title={file.name}
                  >
                    {file.name}
                  </span>
                  {!disabled && onFilesChange && (
                    <button
                      onClick={(e) => handleRemove(e, index)}
                      className="ml-1 hover:text-red-500 transition-colors"
                      type="button"
                      aria-label={`Remove ${file.name}`}
                    >
                      <Icon
                        icon="mdi:close-circle"
                        height={16}
                        aria-hidden="true"
                      />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <input
        type="file"
        id={inputId}
        ref={fileInputRef}
        className="hidden"
        accept={acceptedFileExtensions.join(",")}
        onChange={handleManualFileChange}
        disabled={disabled}
        multiple={multiple}
        aria-label="File upload input"
      />
    </div>
  );
};
